/**
 * Stotram parser — detects shloka structure, uvāca markers, dhyānam sections.
 * Ported in spirit from jnana-native/src/bmScreens/bhajan/view/lyricsRendererView/stotram/parser.ts.
 *
 * Heuristic: a stotram has at least 2 numbered shlokas, often surrounded by
 * "uvāca" speakers and "dhyānam" / "phala śruti" section headers.
 */

export interface StotramSection {
  kind: 'header' | 'shloka';
  /** display label for header (e.g. "śiva uvāca", "dhyānam"); shloka number for shlokas */
  label?: string;
  /** number for shloka sections only */
  number?: number;
  /** raw lines of this section */
  lines: string[];
}

export interface ParsedStotram {
  isStotram: boolean;
  sections: StotramSection[];
}

const SHLOKA_NUMBER_RE = /[\|‖।]\s*([0-9०-९]{1,3})\s*[\|‖।]\s*$/;
const UVACA_RE = /\b(uvāca|uvaca|उवाच|ఉవాచ|உவாச|ഉവാച|ਉਵਾਚ|ઉવાચ|ಉವಾಚ|ৰ্বাচ)\b/i;
const HEADER_PATTERNS = [
  /^\s*(.+?)\s+uvāca\s*$/i,
  /^\s*(.+?)\s+uvaca\s*$/i,
  /^\s*(dhyānam|dhyanam|ध्यानम|ధ్యానం)\s*[:।॥]?\s*$/i,
  /^\s*(phala\s*śruti|phala\s*shruti|फलश्रुति)\s*[:।॥]?\s*$/i,
  /^\s*(maṅgaḷam|mangalam|maṅgalam)\s*[:।॥]?\s*$/i,
];

function devToInt(s: string): number | null {
  // Devanagari digits ०-९ to 0-9
  const map: Record<string, string> = { '०':'0','१':'1','२':'2','३':'3','४':'4','५':'5','६':'6','७':'7','८':'8','९':'9' };
  const norm = s.replace(/[०-९]/g, (c) => map[c] ?? c);
  const n = parseInt(norm, 10);
  return Number.isFinite(n) ? n : null;
}

function isHeaderLine(line: string): { ok: boolean; label?: string } {
  const t = line.trim();
  if (!t) return { ok: false };
  for (const re of HEADER_PATTERNS) {
    const m = t.match(re);
    if (m) return { ok: true, label: t.replace(/[:।॥]\s*$/, '').trim() };
  }
  // standalone "X uvāca" line
  if (UVACA_RE.test(t) && t.length < 80) return { ok: true, label: t };
  return { ok: false };
}

function shlokaNumber(line: string): number | null {
  const m = line.match(SHLOKA_NUMBER_RE);
  if (!m) return null;
  return devToInt(m[1]);
}

export function parseStotram(text: string): ParsedStotram {
  if (!text || !text.trim()) return { isStotram: false, sections: [] };

  const lines = text.split(/\r?\n/);
  const sections: StotramSection[] = [];
  let buffer: string[] = [];
  let shlokaCount = 0;

  const flushBufferAsShloka = (num?: number) => {
    if (buffer.length === 0) return;
    sections.push({ kind: 'shloka', number: num, lines: [...buffer] });
    buffer = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    const header = isHeaderLine(line);
    if (header.ok) {
      flushBufferAsShloka();
      sections.push({ kind: 'header', label: header.label, lines: [] });
      continue;
    }

    buffer.push(line);

    const num = shlokaNumber(line);
    if (num != null) {
      shlokaCount++;
      flushBufferAsShloka(num);
    }
  }
  // any tail content
  if (buffer.length > 0) {
    // if last buffer ends without a number but we already saw shlokas, treat as shloka (closing)
    if (shlokaCount > 0) flushBufferAsShloka();
    else sections.push({ kind: 'shloka', lines: [...buffer] });
  }

  // require ≥2 numbered shlokas to consider it a stotram. Otherwise treat as plain bhajan.
  const isStotram = shlokaCount >= 2;
  return { isStotram, sections };
}
